import { Fragment, Node as PMNode, Schema } from "@tiptap/pm/model";

// Collapse any content into a single paragraph of text + hardBreaks.
// Marks are dropped, block types (headings, lists, tables, etc.) are
// dissolved, and block boundaries become hardBreaks. Empty blocks between
// content preserve the blank line (two consecutive hardBreaks). Non-text,
// non-hardBreak inline nodes (images, embeds) are dropped.
export const flattenToPlainParagraph = (
  fragment: Fragment,
  schema: Schema
): PMNode => {
  const paragraph = schema.nodes.paragraph;
  const hardBreak = schema.nodes.hardBreak;
  if (!paragraph || !hardBreak) {
    throw new Error(
      "flattenToPlainParagraph: schema must define paragraph and hardBreak"
    );
  }

  const inline: PMNode[] = [];
  let pendingBreak = false;

  const flushPending = () => {
    if (pendingBreak) {
      inline.push(hardBreak.create());
      pendingBreak = false;
    }
  };

  const visit = (node: PMNode) => {
    if (node.isText) {
      flushPending();
      inline.push(schema.text(node.text!));
      return;
    }
    if (node.type === hardBreak) {
      flushPending();
      inline.push(hardBreak.create());
      return;
    }
    if (node.isBlock) {
      flushPending();
      node.forEach(visit);
      if (inline.length > 0) pendingBreak = true;
    }
    // other inline leaf nodes (images, embeds) are dropped
  };

  fragment.forEach(visit);

  return paragraph.create(null, Fragment.fromArray(inline));
};

// Collapse consecutive paragraph siblings into a single paragraph separated
// by hardBreaks, matching the component's Enter-is-soft-break keyboard
// behavior. Lists, headings, tables, and other block types are preserved.
export const flattenParagraphSiblings = (
  fragment: Fragment,
  schema: Schema
): Fragment => {
  const paragraph = schema.nodes.paragraph;
  const hardBreak = schema.nodes.hardBreak;
  if (!paragraph || !hardBreak) return fragment;

  const result: PMNode[] = [];
  let pending: PMNode[] | null = null;

  const flushPending = () => {
    if (pending) {
      result.push(paragraph.create(null, Fragment.fromArray(pending)));
      pending = null;
    }
  };

  fragment.forEach((child) => {
    if (child.type === paragraph) {
      const inline: PMNode[] = [];
      child.content.forEach((c) => inline.push(c));
      if (pending === null) {
        pending = inline;
      } else {
        pending.push(hardBreak.create());
        pending.push(...inline);
      }
    } else {
      flushPending();
      result.push(child.copy(flattenParagraphSiblings(child.content, schema)));
    }
  });
  flushPending();

  return Fragment.fromArray(result);
};
